import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { UserProfileResponse, BIBLE_BOOKS } from '../../../shared/types';
import BottomNav from '../components/BottomNav';
import ChapterProgressGrid from '../components/ChapterProgressGrid';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastReadDate: string | null;
}

interface RawSession {
  sessionId?: string;
  createdAt: string;
  bookRead?: string;
  chaptersRead?: number[];
  actualTimeSpent?: number;
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function chapterLabel(chapters: number[]) {
  if (!chapters?.length) return '';
  const s = [...chapters].sort((a, b) => a - b);
  if (s.length === 1) return `Ch. ${s[0]}`;
  const contiguous = s.every((c, i) => i === 0 || c === s[i - 1] + 1);
  return contiguous ? `Ch. ${s[0]}–${s[s.length - 1]}` : `Ch. ${s.join(', ')}`;
}

const AppPage = () => {
  const navigate = useNavigate();
  const { getSessions, getStreak, getProfile, getProgress } = useApi();

  const [streak, setStreak] = useState<StreakData>({ currentStreak: 0, longestStreak: 0, lastReadDate: null });
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [todaySessions, setTodaySessions] = useState<RawSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookCompletedChapters, setBookCompletedChapters] = useState<Set<number>>(new Set());

  // Reading setup state
  const [book, setBook] = useState('');
  const [bookQuery, setBookQuery] = useState('');
  const [showBookDropdown, setShowBookDropdown] = useState(false);
  const [startChapter, setStartChapter] = useState(1);

  useEffect(() => {
    Promise.all([
      getSessions(),
      getStreak().catch(() => ({ currentStreak: 0, longestStreak: 0, lastReadDate: null })),
      getProfile().catch(() => null),
    ]).then(([sessionData, streakData, profileData]: [any[], any, any]) => {
      setStreak(streakData);
      setProfile(profileData);
      const today = todayISO();
      const filtered = (sessionData ?? []).filter(
        (s: RawSession) => s.createdAt?.startsWith(today)
      );
      setTodaySessions(filtered);

      // Fetch chapter progress for the current book
      if (profileData?.currentBook) {
        const rt = profileData.currentReadThrough ?? 1;
        getProgress(profileData.currentBook, rt).then((progressData) => {
          if (progressData?.completedChapters) {
            const nums = new Set<number>(
              progressData.completedChapters.map((c: { chapter: number }) => c.chapter)
            );
            setBookCompletedChapters(nums);
          }
        }).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Pre-fill book/chapter once profile loads
  useEffect(() => {
    if (profile?.currentBook) {
      setBook(profile.currentBook);
      setBookQuery(profile.currentBook);
    }
    if (profile?.currentChapter) {
      setStartChapter(profile.currentChapter);
    }
  }, [profile]);

  const handleStartReading = () => {
    navigate('/timer', { state: { book, startChapter } });
  };

  const filteredBooks = bookQuery.trim().length > 0
    ? BIBLE_BOOKS.filter(
        (b) =>
          b.name.toLowerCase().includes(bookQuery.toLowerCase()) ||
          b.abbreviation.toLowerCase().includes(bookQuery.toLowerCase())
      )
    : BIBLE_BOOKS;

  const selectedBookData = BIBLE_BOOKS.find((b) => b.name === book);
  const maxChapters = selectedBookData?.chapters ?? 1;

  const readToday = streak.lastReadDate === todayISO();

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-leather border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment-50 pb-20">
      {/* Header */}
      <div className="bg-parchment-100 border-b border-parchment-200 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <span className="text-2xl">📖</span>
          <h1 className="text-xl font-bold text-ink">TrackBible</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 flex flex-col gap-6">

        {/* Streak pill */}
        <div className="flex items-center justify-center">
          <div className="inline-flex items-center bg-parchment-100 border border-parchment-200 rounded-full px-4 py-2 shadow-sm">
            {readToday ? (
              <span className="text-sm text-leather font-semibold">
                ✅ Read today · {streak.currentStreak} day streak
              </span>
            ) : streak.currentStreak > 0 ? (
              <span className="text-sm text-ink-muted font-medium">
                🔥 {streak.currentStreak}-day streak — keep it going!
              </span>
            ) : (
              <span className="text-sm text-ink-muted font-medium">
                Start your streak today
              </span>
            )}
          </div>
        </div>

        {/* Reading CTA card */}
        <div className="bg-parchment-100 rounded-2xl border border-parchment-200 shadow-sm p-6 flex flex-col gap-5">
          <h2 className="text-lg font-bold text-ink text-center">What would you like to read?</h2>

          {/* Book search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search book (e.g. gen, matt)…"
              value={bookQuery}
              onChange={(e) => {
                setBookQuery(e.target.value);
                setBook('');
                setShowBookDropdown(true);
              }}
              onFocus={() => setShowBookDropdown(true)}
              onBlur={() => setTimeout(() => setShowBookDropdown(false), 150)}
              className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm text-ink bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
            />
            {showBookDropdown && filteredBooks.length > 0 && (
              <ul className="absolute z-50 w-full mt-1 bg-parchment-50 border border-parchment-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredBooks.map((b) => (
                  <li key={b.name}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setBook(b.name);
                        setBookQuery(b.name);
                        setStartChapter(1);
                        setShowBookDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-parchment-200 hover:text-leather flex items-center justify-between"
                    >
                      <span>{b.name}</span>
                      <span className="text-xs text-ink-muted">{b.chapters} ch · {b.testament === 'old' ? 'OT' : 'NT'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Starting chapter + progress grid */}
          {book && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-ink-muted whitespace-nowrap">Starting chapter</label>
                <input
                  type="number"
                  min={1}
                  max={maxChapters}
                  value={startChapter}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(maxChapters, Number(e.target.value) || 1));
                    setStartChapter(v);
                  }}
                  className="w-20 border border-parchment-300 rounded-lg px-2 py-1.5 text-sm text-center bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
                <span className="text-xs text-ink-muted">/ {maxChapters}</span>
              </div>
              <ChapterProgressGrid
                book={book}
                completedChapters={bookCompletedChapters}
                readThrough={profile?.currentReadThrough ?? 1}
              />
            </div>
          )}

          {/* Start button */}
          <button
            onClick={handleStartReading}
            className="w-full bg-leather hover:bg-leather-light active:scale-95 text-parchment-100 font-semibold text-lg py-4 rounded-xl shadow-sm transition-all duration-150"
          >
            Start Reading →
          </button>
        </div>

        {/* Today's sessions */}
        {todaySessions.length > 0 && (
          <div>
            <h2 className="text-xs font-semibold text-ink-muted uppercase tracking-widest mb-3">
              Today's Sessions
            </h2>
            <div className="flex flex-col gap-2">
              {todaySessions.map((s, i) => (
                <div
                  key={s.sessionId ?? i}
                  className="bg-parchment-100 rounded-xl border border-parchment-200 px-4 py-3 flex items-center justify-between shadow-sm"
                >
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      {s.bookRead ?? 'Unknown'}
                      {s.chaptersRead?.length ? (
                        <span className="text-ink-muted font-normal"> · {chapterLabel(s.chaptersRead)}</span>
                      ) : null}
                    </p>
                  </div>
                  {(s.actualTimeSpent ?? 0) > 0 && (
                    <span className="text-xs font-semibold text-leather bg-parchment-200 rounded-full px-3 py-1 shrink-0">
                      {s.actualTimeSpent}m
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {todaySessions.length === 0 && streak.currentStreak === 0 && !profile?.currentBook && (
          <div className="bg-parchment-100 rounded-2xl border border-parchment-200 shadow-sm p-6 text-center flex flex-col gap-3">
            <span className="text-4xl">📖</span>
            <h3 className="text-lg font-bold text-ink">Welcome to TrackBible!</h3>
            <p className="text-sm text-ink-muted leading-relaxed">
              Pick a book above and tap <span className="font-semibold text-leather">Start Reading</span> to log your first session and begin your streak.
            </p>
          </div>
        )}

        {todaySessions.length === 0 && (streak.currentStreak > 0 || !!profile?.currentBook) && (
          <p className="text-sm text-ink-muted text-center">
            No sessions yet today — start one above!
          </p>
        )}

      </div>

      <BottomNav />
    </div>
  );
};

export default AppPage;
